export type TermSheetSignState = {
  termSheetOverride?: boolean
  termSheetWetSigned?: boolean
  termSheetSignedPdfKey?: string
  termSheetSignedDate?: string
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/

/** True if value is YYYY-MM-DD or a legacy numeric ms timestamp. */
export const hasTermSheetSignedDate = (value?: string): boolean => {
  if (!value) return false
  if (YMD_RE.test(value)) return true
  return /^\d+$/.test(value) && Number(value) > 0
}

/** Normalize stored value for a date input (YYYY-MM-DD). Legacy ms → calendar date. */
export const toTermSheetSignedDateInputValue = (value?: string): string => {
  if (!value) return ''
  if (YMD_RE.test(value)) return value
  if (/^\d+$/.test(value)) {
    const d = new Date(Number(value))
    if (Number.isNaN(d.getTime())) return ''
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }
  return ''
}

/** Cleared when a new unsigned Loan Term Sheet is generated (not Pre-Approval, not e-sign). */
export const emptyTermSheetSignState = (): Required<TermSheetSignState> => ({
  termSheetSignedDate: '',
  termSheetSignedPdfKey: '',
  termSheetWetSigned: false,
  termSheetOverride: false,
})

/**
 * Workflow gate "Borrower Has Signed Term Sheet" is satisfied by override,
 * wet-sign with an uploaded PDF, or e-sign (date + PDF).
 */
export const isBorrowerTermSheetSatisfied = (p: TermSheetSignState): boolean => {
  if (p.termSheetOverride) return true
  if (p.termSheetWetSigned && !!p.termSheetSignedPdfKey) return true
  return hasTermSheetSignedDate(p.termSheetSignedDate) && !!p.termSheetSignedPdfKey
}

export const getTermSheetSignStatusLabel = (p: TermSheetSignState): string => {
  if (p.termSheetOverride) return 'Overridden'
  if (p.termSheetWetSigned && !!p.termSheetSignedPdfKey) return 'Wet signed'
  if (hasTermSheetSignedDate(p.termSheetSignedDate) && p.termSheetSignedPdfKey) return 'Signed'
  return 'Unsigned'
}
